import { useCallback, useEffect, useRef, useState } from "react";
import i18n from "src/i18n/config";
import type { ApiLang } from "src/utils/apiLang";
import { formatDashboardValueString } from "src/utils/formatDashboardValue";
import {
  organicMediaService,
  type ContentTrendKpiPayload,
  type ContentTrendKpiPoint,
  type EngagementTrendPoint,
  type FacebookContentReportResponse,
  type FollowersTrendPayload,
  type FollowersReportResponse,
  type InstagramContentReportResponse,
  type InstagramFollowersReportResponse,
  type TrendPoint,
} from "src/services/OrganicMediaAPIs";

type ContentTrendGranularity = "daily" | "monthly";

const POST_TYPE_COLORS = [
  "#0f4a97",
  "#3a6faf",
  "#5f8ebf",
  "#7da8cc",
  "#90bad9",
  "#4a78a8",
  "#628bb0",
  "#5673bf",
  "#a6c9e8",
  "#4d79a5",
  "#12a8ca",
];

// The month-over-month trend series carries its own percentage change on the
// latest point (`moMPercent`); used as a fallback when the API doesn't also
// send a flat `percentageChange`/`change_percent` field alongside a metric.
function latestMoMPercent(trendData: EngagementTrendPoint[] | undefined): number | undefined {
  if (!Array.isArray(trendData) || trendData.length === 0) {
    return undefined;
  }
  return trendData[trendData.length - 1]?.moMPercent;
}

// An absent percentageChange/change_percent field falls back to deriving a
// change from trendData. An explicit `null` means the backend has no
// comparison for this period (e.g. no prior period) — hidden entirely rather
// than shown as a flat 0.0%. A real `0` from the API is a genuine "no change"
// and is shown as-is.
function resolvePercentageChange(
  percentageChange: number | null | undefined,
  changePercent: number | null | undefined,
  trendData: EngagementTrendPoint[] | undefined
): number | undefined {
  if (percentageChange !== undefined) {
    return percentageChange ?? undefined;
  }
  if (changePercent !== undefined) {
    return changePercent ?? undefined;
  }
  return latestMoMPercent(trendData);
}

function resolveKpiMetricKey(card: {
  id?: string | null;
  metric_key?: string | null;
  metricKey?: string | null;
  metric?: string | null;
  title: string;
}): string {
  const candidate = card.id ?? card.metric_key ?? card.metricKey ?? card.metric;
  if (typeof candidate === "string" && candidate.trim()) {
    return candidate.trim().toLowerCase().replace(/[\s-]+/g, "_");
  }
  return card.title.trim().toLowerCase();
}

function normalizeKpiCard(card: {
  title: string;
  value: string;
  comparison_label?: string;
  comparisonLabel?: string;
  tooltip_label?: string;
  tooltipLabel?: string;
  trend: Array<{ date: string; value: number }>;
  trendData?: EngagementTrendPoint[];
  change_percent?: number | null;
  percentageChange?: number | null;
  percentageChangeFormattedValue?: string | null;
  id?: string | null;
  metric_key?: string | null;
  metricKey?: string | null;
  metric?: string | null;
}) {
  const percentageChange = resolvePercentageChange(
    card.percentageChange,
    card.change_percent,
    card.trendData
  );
  return {
    title: card.title,
    // Stable, language-independent match key — use this instead of `title`
    // to identify which metric a card is (see `resolveKpiMetricKey`).
    metricKey: resolveKpiMetricKey(card),
    // Already a backend-localized display string (Arabic-Indic digits for
    // lang=ar) — this only adds thousands separators to embedded 4+ digit
    // runs and is a no-op on non-ASCII digits, so it's safe either way.
    value: formatDashboardValueString(card.value),
    comparisonLabel:
      card.comparisonLabel ??
      card.comparison_label ??
      i18n.t("organicMedia.facebook.vsLastPeriod"),
    tooltipLabel: card.tooltipLabel ?? card.tooltip_label ?? card.title,
    trend: card.trend ?? [],
    percentageChange: percentageChange ?? undefined,
    // `null` means "no prior period" (mirrors percentageChange); `undefined`
    // means the backend hasn't sent this field yet — components fall back to
    // formatting `percentageChange` themselves in that case.
    percentageChangeFormattedValue: card.percentageChangeFormattedValue,
  };
}

function getContentTrendSeries(
  payload: ContentTrendKpiPayload | undefined,
  granularity: ContentTrendGranularity,
): ContentTrendKpiPoint[] {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return payload[granularity] ?? [];
}

function getFollowersTrendSeries(
  payload: FollowersTrendPayload | undefined,
  granularity: FollowersTrendGranularity,
): TrendPoint[] {
  if (!payload) {
    return [];
  }

  return payload[granularity] ?? [];
}

function buildMetricTrend(
  trendSeries: ContentTrendKpiPoint[],
  keys: Array<keyof ContentTrendKpiPoint>,
) {
  return trendSeries.map(row => ({
    date: row.date,
    value: numberValue(...keys.map(key => row[key])),
  }));
}

function hasTrendMetric(
  trendSeries: ContentTrendKpiPoint[],
  keys: Array<keyof ContentTrendKpiPoint>,
) {
  return trendSeries.some(row =>
    keys.some(key => row[key] !== undefined && row[key] !== null)
  );
}

function withContentTrendFallback<T extends ReturnType<typeof normalizeKpiCard>>(
  kpis: T[],
  trendSeries: ContentTrendKpiPoint[],
): T[] {
  if (trendSeries.length === 0) {
    return kpis;
  }

  return kpis.map(card => {

    const normalizedTitle = card.metricKey.replace(/_/g, " ");
    if (normalizedTitle.includes("impression")) {
      const keys: Array<keyof ContentTrendKpiPoint> = ["impressions"];
      if (!hasTrendMetric(trendSeries, keys)) {
        return card;
      }
      return {
        ...card,
        trend: buildMetricTrend(trendSeries, keys),
      };
    }
    if (normalizedTitle.includes("reach")) {
      const keys: Array<keyof ContentTrendKpiPoint> = ["reach"];
      if (!hasTrendMetric(trendSeries, keys)) {
        return card;
      }
      return {
        ...card,
        trend: buildMetricTrend(trendSeries, keys),
      };
    }
    if (normalizedTitle.includes("engagement")) {
      const keys: Array<keyof ContentTrendKpiPoint> = ["engagement_rate", "engagementRate"];
      if (!hasTrendMetric(trendSeries, keys)) {
        return card;
      }
      return {
        ...card,
        trend: buildMetricTrend(trendSeries, keys),
      };
    }
    // "Posts Created" is relabeled to "Viewers" in the Trends chart; its
    // per-day values come from the aggregate trend series' `viewers` field
    // rather than a raw post count.
    if (normalizedTitle.includes("posts created")) {
      const keys: Array<keyof ContentTrendKpiPoint> = ["viewers"];
      if (!hasTrendMetric(trendSeries, keys)) {
        return card;
      }
      return {
        ...card,
        trend: buildMetricTrend(trendSeries, keys),
      };
    }

    return card;
  });
}

function normalizeEngagementMetric(
  metricKey: string,
  metric:
    | number
    | {
        value?: number;
        count?: number;
        total?: number;
        change_percent?: number | null;
        percentageChange?: number | null;
        percentageChangeFormattedValue?: string | null;
        comparison_label?: string;
        comparisonLabel?: string;
        trend?: TrendPoint[];
        trendData?: EngagementTrendPoint[];
        valueFormattedValue?: string;
      }
    | undefined,
  vsLastPeriodKey: "organicMedia.facebook.vsLastPeriod" | "organicMedia.instagram.vsLastPeriod",
  // Used only when `metric` resolves to a plain number (e.g. a reaction count
  // pulled straight off `reaction.like`) — its formatted sibling lives as a
  // flat key on the parent `reaction`/`reactions` object rather than nested
  // inside an EngagementMetricValue, so the caller passes it in explicitly.
  explicitValueFormattedValue?: string,
) {
  const vsDefault = i18n.t(vsLastPeriodKey);
  if (typeof metric === "number") {
    return {
      metricKey,
      value: metric,
      valueFormattedValue: explicitValueFormattedValue,
      percentageChange: undefined,
      percentageChangeFormattedValue: undefined,
      comparisonLabel: vsDefault,
      trend: [] as TrendPoint[],
    };
  }

  const rawChange = resolvePercentageChange(
    metric?.percentageChange,
    metric?.change_percent,
    metric?.trendData
  );
  const percentageChange =
    typeof rawChange === "number" && Number.isFinite(rawChange) ? rawChange : undefined;

  return {
    metricKey,
    value: Number(metric?.value ?? metric?.count ?? metric?.total ?? 0),
    valueFormattedValue: metric?.valueFormattedValue ?? explicitValueFormattedValue,
    percentageChange,
    percentageChangeFormattedValue: metric?.percentageChangeFormattedValue,
    comparisonLabel:
      metric?.comparisonLabel ?? metric?.comparison_label ?? vsDefault,
    trend: (metric && Array.isArray(metric.trend) ? metric.trend : []) as TrendPoint[],
  };
}

function numberValue(...values: unknown[]): number {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

function stringValue(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value;
    }
  }
  return "";
}

function resolveExplicitPostUrl(...values: unknown[]): string {
  const explicitUrl = stringValue(...values);
  return /^https?:\/\//i.test(explicitUrl) ? explicitUrl : "";
}

export function usePostCaptions(startDate: string, endDate: string, lang: ApiLang) {
  const [postCaptions, setPostCaptions] = useState<string[]>(["(All)"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const fetchCaptions = useCallback(async () => {
    if (!startDate || !endDate) {return;}
    setLoading(true);
    setError(null);
    try {
      const { post_captions } = await organicMediaService.fetchPostCaptions({
        start_date: startDate,
        end_date: endDate,
        lang,
      });
      setPostCaptions(
        Array.isArray(post_captions) && post_captions.length > 0
          ? ["(All)", ...post_captions.filter(caption => caption !== "(All)")]
          : ["(All)"]
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("organicMedia.errors.postCaptions"));
      setPostCaptions(["(All)"]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, lang]);

  useEffect(() => {
    const fetchKey = `${startDate}|${endDate}|${lang}`;
    if (lastFetchKeyRef.current === fetchKey) {return;}
    lastFetchKeyRef.current = fetchKey;
    fetchCaptions();
  }, [fetchCaptions, startDate, endDate, lang]);

  return { postCaptions, loading, error, refetch: fetchCaptions };
}

export function useInstagramPostCaptions(startDate: string, endDate: string, lang: ApiLang) {
  const [postCaptions, setPostCaptions] = useState<string[]>(["(All)"]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const fetchCaptions = useCallback(async () => {
    if (!startDate || !endDate) {return;}
    setLoading(true);
    setError(null);
    try {
      const { post_captions } = await organicMediaService.fetchInstagramPostCaptions({
        start_date: startDate,
        end_date: endDate,
        lang,
      });
      setPostCaptions(
        Array.isArray(post_captions) && post_captions.length > 0
          ? ["(All)", ...post_captions.filter(caption => caption !== "(All)")]
          : ["(All)"]
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("organicMedia.errors.instagramPostCaptions"));
      setPostCaptions(["(All)"]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, lang]);

  useEffect(() => {
    const fetchKey = `${startDate}|${endDate}|${lang}`;
    if (lastFetchKeyRef.current === fetchKey) {return;}
    lastFetchKeyRef.current = fetchKey;
    fetchCaptions();
  }, [fetchCaptions, startDate, endDate, lang]);

  return { postCaptions, loading, error, refetch: fetchCaptions };
}

export function useFacebookContentReport(
  startDate: string,
  endDate: string,
  postCaption: string,
  lang: ApiLang,
  trendGranularity: ContentTrendGranularity = "daily",
) {
  const [data, setData] = useState<FacebookContentReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) {return;}
    setLoading(true);
    setError(null);
    try {
      const response = await organicMediaService.fetchFacebookContentReport({
        start_date: startDate,
        end_date: endDate,
        post_caption: postCaption === "(All)" ? undefined : postCaption,
        lang,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("organicMedia.errors.facebookContent"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, postCaption, lang]);

  useEffect(() => {
    const fetchKey = `${startDate}|${endDate}|${postCaption}|${lang}`;
    if (lastFetchKeyRef.current === fetchKey) {return;}
    lastFetchKeyRef.current = fetchKey;
    fetchReport();
  }, [fetchReport, startDate, endDate, postCaption, lang]);

  const contentTrendSeries = getContentTrendSeries(
    data?.trend_kpi ?? data?.trends_kpi,
    trendGranularity,
  );
  const kpis = withContentTrendFallback(
    (data?.kpis ?? []).map(normalizeKpiCard),
    contentTrendSeries,
  );
  const totalEngagements = data?.total_engagements
    ? (() => {
      const reactions = data.total_engagements.reaction ?? {};
      return [
        normalizeEngagementMetric(
          "viewers",
          data.total_engagements.viewers ??
            data.total_engagements.impressions ??
            data.total_engagements.reach,
          "organicMedia.facebook.vsLastPeriod",
        ),
        normalizeEngagementMetric(
          "link_clicks",
          data.total_engagements.link_clicks ?? data.total_engagements.clicks,
          "organicMedia.facebook.vsLastPeriod",
        ),
        normalizeEngagementMetric(
          "shares",
          data.total_engagements.shares ?? reactions.shares,
          "organicMedia.facebook.vsLastPeriod",
          reactions.sharesFormattedValue,
        ),
        normalizeEngagementMetric(
          "comments",
          data.total_engagements.comments,
          "organicMedia.facebook.vsLastPeriod",
        ),
        normalizeEngagementMetric(
          "like",
          reactions.like,
          "organicMedia.facebook.vsLastPeriod",
          reactions.likeFormattedValue,
        ),
        normalizeEngagementMetric(
          "love",
          reactions.love ?? data.total_engagements.love,
          "organicMedia.facebook.vsLastPeriod",
          reactions.loveFormattedValue,
        ),
        normalizeEngagementMetric(
          "angry",
          reactions.angry ?? data.total_engagements.angry,
          "organicMedia.facebook.vsLastPeriod",
          reactions.angryFormattedValue,
        ),
        normalizeEngagementMetric(
          "wow",
          reactions.wow ?? data.total_engagements.wow,
          "organicMedia.facebook.vsLastPeriod",
          reactions.wowFormattedValue,
        ),
        normalizeEngagementMetric(
          "haha",
          reactions.haha ?? data.total_engagements.haha,
          "organicMedia.facebook.vsLastPeriod",
          reactions.hahaFormattedValue,
        ),
        normalizeEngagementMetric(
          "sorry",
          reactions.sorry,
          "organicMedia.facebook.vsLastPeriod",
          reactions.sorryFormattedValue,
        ),
      ];
    })()
    : [];

  // The aggregate reactions change lives on `reaction.percentageChange`/`change_percent`,
  // a sibling of the individual like/love/haha/etc counts rather than nested under each
  // of them; only fall back to deriving it from `trendData` when the API omits it.
  const reactionsPercentageChange = resolvePercentageChange(
    data?.total_engagements?.reaction?.percentageChange,
    data?.total_engagements?.reaction?.change_percent,
    data?.total_engagements?.reaction?.trendData
  );
  const reactionsPercentageChangeFormattedValue =
    data?.total_engagements?.reaction?.percentageChangeFormattedValue;

  const postDetails = (data?.post_details ?? []).map(p => {
    const impressions = numberValue(p.viewers, p.impressions, p.reach, p.organic_reach);
    const clicks = numberValue(p.link_clicks, p.clicks);
    const reactions = numberValue(p.loves, p.likes);
    const comments = numberValue(p.comments);
    const shares = numberValue(p.shares);
    const engagementRate = numberValue(
      p.engagement_rate,
      p.engagementRate,
      impressions > 0 ? ((clicks + reactions + comments + shares) / impressions) * 100 : 0,
    );

    return {
      postSystemId: stringValue(p.post_system_id, p.post_id, p.id),
      caption: stringValue(p.caption, p.message),
      postType: stringValue(p.post_type, p.type),
      engagementRate,
      engagementRateFormattedValue: p.engagement_rateFormattedValue,
      ctr: numberValue(p.ctr, impressions > 0 ? (clicks / impressions) * 100 : 0),
      ctrFormattedValue: p.ctrFormattedValue,
      impressions,
      impressionsFormattedValue: p.impressionsFormattedValue,
      reach: numberValue(p.reach, p.organic_reach, p.viewers, p.impressions),
      clicks,
      clicksFormattedValue: p.link_clicksFormattedValue,
      comments,
      commentsFormattedValue: p.commentsFormattedValue,
      loves: reactions,
      lovesFormattedValue: p.likesFormattedValue,
      shares,
      sharesFormattedValue: p.sharesFormattedValue,
      postUrl: resolveExplicitPostUrl(
        p.permalink_url,
        p.post_url,
        p.permalink,
        p.url,
        p.link,
      ),
      thumbnailUrl: stringValue(
        p.thumbnail_url,
        p.thumbnail,
        p.full_picture,
        p.picture,
        p.media_url,
        p.image_url,
      ),
      createdAt: stringValue(p.created_time, p.created_at, p.date),
    };
  });

  const derivedPostsByType = Array.from(
    postDetails.reduce((map, post) => {
      const type = post.postType || "unknown";
      map.set(type, (map.get(type) ?? 0) + 1);
      return map;
    }, new Map<string, number>())
  ).map(([name, value], idx) => ({
    name,
    value,
    valueFormattedValue: undefined as string | undefined,
    color: POST_TYPE_COLORS[idx % POST_TYPE_COLORS.length],
  }));

  const postsByType = (data?.posts_by_type?.length ? data.posts_by_type : derivedPostsByType).map((item, idx) => ({
    name: item.name,
    value: item.value,
    valueFormattedValue: item.valueFormattedValue,
    color: item.color ?? POST_TYPE_COLORS[idx % POST_TYPE_COLORS.length],
  }));

  const postTypeOverTime = data?.post_type_over_time ?? [];
  const postTypeMetrics = data?.post_type_metrics?.length
    ? data.post_type_metrics.map(row => ({
      type: row.type,
      engagementRate: numberValue(row.engagementRate),
      engagementRateFormattedValue: row.engagementRateFormattedValue,
      organicReach: numberValue(row.viewers, row.organicReach, row.impressions),
      ctr: numberValue(row.ctr),
      ctrFormattedValue: row.ctrFormattedValue,
      impressions: numberValue(row.impressions, row.organicReach),
      impressionsFormattedValue: row.impressionsFormattedValue ?? row.viewersFormattedValue,
      clicks: numberValue(row.link_clicks, row.clicks),
      clicksFormattedValue: row.link_clicksFormattedValue,
    }))
    : Array.from(
      postDetails.reduce((map, post) => {
        const type = post.postType || "unknown";
        const current = map.get(type) ?? {
          type,
          engagementTotal: 0,
          posts: 0,
          organicReach: 0,
          impressions: 0,
          clicks: 0,
        };
        current.engagementTotal += numberValue(post.engagementRate);
        current.posts += 1;
        current.organicReach += numberValue(post.reach, post.impressions);
        current.impressions += numberValue(post.impressions, post.reach);
        current.clicks += numberValue(post.clicks);
        map.set(type, current);
        return map;
      }, new Map<string, {
        type: string;
        engagementTotal: number;
        posts: number;
        organicReach: number;
        impressions: number;
        clicks: number;
      }>())
    ).map(([, row]) => ({
      type: row.type,
      engagementRate: row.posts > 0 ? row.engagementTotal / row.posts : 0,
      engagementRateFormattedValue: undefined as string | undefined,
      organicReach: row.organicReach,
      ctr: row.impressions > 0 ? (row.clicks / row.impressions) * 100 : 0,
      ctrFormattedValue: undefined as string | undefined,
      impressions: row.impressions || row.organicReach,
      impressionsFormattedValue: undefined as string | undefined,
      clicks: row.clicks,
      clicksFormattedValue: undefined as string | undefined,
    }));

  return {
    kpis,
    totalEngagements,
    reactionsPercentageChange,
    reactionsPercentageChangeFormattedValue,
    postsByType,
    postTypeOverTime,
    postTypeMetrics,
    postDetails,
    loading,
    error,
    refetch: fetchReport,
  };
}

export function useInstagramContentReport(
  startDate: string,
  endDate: string,
  postCaption: string,
  lang: ApiLang,
  trendGranularity: ContentTrendGranularity = "daily",
) {
  const [data, setData] = useState<InstagramContentReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) {return;}
    setLoading(true);
    setError(null);
    try {
      const response = await organicMediaService.fetchInstagramContentReport({
        start_date: startDate,
        end_date: endDate,
        post_caption: postCaption === "(All)" ? undefined : postCaption,
        lang,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("organicMedia.errors.instagramContent"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, postCaption, lang]);

  useEffect(() => {
    const fetchKey = `${startDate}|${endDate}|${postCaption}|${lang}`;
    if (lastFetchKeyRef.current === fetchKey) {return;}
    lastFetchKeyRef.current = fetchKey;
    fetchReport();
  }, [fetchReport, startDate, endDate, postCaption, lang]);

  const selectedContentTrendSeries = getContentTrendSeries(
    data?.trend_kpi ?? data?.trends_kpi,
    trendGranularity,
  );
  const contentTrendSeries =
    selectedContentTrendSeries.length > 0
      ? selectedContentTrendSeries
      : data?.impressions_vs_reach_trend ?? [];
  const kpisWithTrendFallback = withContentTrendFallback(
    (data?.kpis ?? []).map(normalizeKpiCard),
    contentTrendSeries,
  );
  const totalEngagements = data?.total_engagements
    ? (
        [
          ["impressions", data.total_engagements.impressions, undefined],
          [
            "likes",
            data.total_engagements.likes ?? data.total_engagements.reactions?.like,
            data.total_engagements.reactions?.likeFormattedValue,
          ],
          ["shares", data.total_engagements.shares, undefined],
          ["comments", data.total_engagements.comments, undefined],
          ["saves", data.total_engagements.saves, undefined],
          ["profile_visits", data.total_engagements.profile_visits, undefined],
          ["new_followers", data.total_engagements.new_followers, undefined],
        ] as const
      )
        // Only surface a card when the backend actually returned that metric —
        // a key missing from the response (rather than present with a 0 value)
        // should drop the card instead of rendering a fake "0".
        .filter(([, raw]) => raw !== undefined && raw !== null)
        .map(([key, raw, explicitFormatted]) =>
          normalizeEngagementMetric(key, raw, "organicMedia.instagram.vsLastPeriod", explicitFormatted),
        )
    : [];

  // The aggregate Instagram reactions change lives on `reactions.percentageChange`/
  // `change_percent`, a sibling of the individual `like` count rather than nested
  // under it; only fall back to deriving it from `trendData` when the API omits it.
  const reactionsPercentageChange = resolvePercentageChange(
    data?.total_engagements?.reactions?.percentageChange,
    data?.total_engagements?.reactions?.change_percent,
    data?.total_engagements?.reactions?.trendData
  );
  const reactionsPercentageChangeFormattedValue =
    data?.total_engagements?.reactions?.percentageChangeFormattedValue;

  const postsByType = (data?.posts_by_type ?? []).map((item, idx) => ({
    name: item.name,
    value: item.value,
    valueFormattedValue: item.valueFormattedValue,
    color: item.color ?? POST_TYPE_COLORS[idx % POST_TYPE_COLORS.length],
  }));

  const postTypeOverTime = data?.post_type_over_time ?? [];
  const postTypeMetrics = (data?.post_type_metrics ?? []).map(row => ({
    type: row.type,
    engagementRate: row.engagementRate ?? 0,
    engagementRateFormattedValue: row.engagementRateFormattedValue,
    organicReach: row.reach ?? 0,
    organicReachFormattedValue: row.reachFormattedValue,
    ctr: 0,
    impressions: row.impressions ?? 0,
    impressionsFormattedValue: row.impressionsFormattedValue,
    clicks: 0,
  }));

  const postDetails = (data?.post_details ?? []).map(p => {
    const impressions = numberValue(p.impressions);
    const reach = numberValue(p.reach);
    const likes = numberValue(p.likes, p.reactions?.like);
    const comments = numberValue(p.comments);
    const shares = numberValue(p.shares);
    const saves = numberValue(p.saves);
    const engagementRate = numberValue(
      p.engagement_rate,
      p.engagementRate,
      impressions > 0
        ? ((likes + comments + shares + saves) / impressions) * 100
        : reach > 0
          ? ((likes + comments + shares + saves) / reach) * 100
          : 0,
    );

    return {
      postSystemId: stringValue(p.post_system_id, p.post_id, p.id),
      caption: stringValue(p.caption, p.message),
      postType: stringValue(p.media_type, p.post_type, p.type),
      engagementRate,
      engagementRateFormattedValue: p.engagement_rateFormattedValue,
      impressions,
      impressionsFormattedValue: p.impressionsFormattedValue,
      reach,
      reachFormattedValue: p.reachFormattedValue,
      likes,
      likesFormattedValue: p.likesFormattedValue ?? p.reactions?.likeFormattedValue,
      comments,
      commentsFormattedValue: p.commentsFormattedValue,
      shares,
      sharesFormattedValue: p.sharesFormattedValue,
      saves,
      savesFormattedValue: p.savesFormattedValue,
      postUrl: resolveExplicitPostUrl(
        p.permalink_url,
        p.post_url,
        p.permalink,
        p.url,
        p.link,
      ),
      thumbnailUrl: stringValue(
        p.thumbnail_url,
        p.thumbnail,
        p.media_url,
        p.image_url,
      ),
      createdAt: stringValue(p.created_time, p.created_at, p.date),
    };
  });

  return {
    kpis: kpisWithTrendFallback,
    totalEngagements,
    reactionsPercentageChange,
    reactionsPercentageChangeFormattedValue,
    postsByType,
    postTypeOverTime,
    postTypeMetrics,
    postDetails,
    loading,
    error,
    refetch: fetchReport,
  };
}

/** Keeps the country/city map sliders aligned with the API payload. */
const FACEBOOK_FOLLOWERS_TOP_GEO_LIMIT = 10;

export type FollowersTrendGranularity = "daily" | "monthly";

export function useFacebookFollowersReport(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  trendGranularity: FollowersTrendGranularity = "daily",
) {
  const [data, setData] = useState<FollowersReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) {return;}
    setLoading(true);
    setError(null);
    try {
      const response = await organicMediaService.fetchFacebookFollowersReport({
        start_date: startDate,
        end_date: endDate,
        lang,
        top_cities: FACEBOOK_FOLLOWERS_TOP_GEO_LIMIT,
        top_countries: FACEBOOK_FOLLOWERS_TOP_GEO_LIMIT,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("organicMedia.errors.facebookFollowers"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, lang]);

  useEffect(() => {
    const fetchKey = `${startDate}|${endDate}|${lang}`;
    if (lastFetchKeyRef.current === fetchKey) {return;}
    lastFetchKeyRef.current = fetchKey;
    fetchReport();
  }, [fetchReport, startDate, endDate, lang]);

  const kpis = (data?.kpis ?? []).map(normalizeKpiCard);
  const countries = data?.countries ?? [];
  const cities = data?.cities ?? [];
  const followerTrend = getFollowersTrendSeries(data?.follower_trend, trendGranularity);

  return {
    kpis,
    countries,
    cities,
    followerTrend,
    loading,
    error,
    refetch: fetchReport,
  };
}


const INSTAGRAM_FOLLOWERS_TOP_GEO_LIMIT = 10;

export function useInstagramFollowersReport(
  startDate: string,
  endDate: string,
  lang: ApiLang,
  trendGranularity: FollowersTrendGranularity = "daily",
) {
  const [data, setData] = useState<InstagramFollowersReportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchKeyRef = useRef<string>("");

  const fetchReport = useCallback(async () => {
    if (!startDate || !endDate) {return;}
    setLoading(true);
    setError(null);
    try {
      const response = await organicMediaService.fetchInstagramFollowersReport({
        start_date: startDate,
        end_date: endDate,
        lang,
        top_cities: INSTAGRAM_FOLLOWERS_TOP_GEO_LIMIT,
        top_countries: INSTAGRAM_FOLLOWERS_TOP_GEO_LIMIT,
      });
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("organicMedia.errors.instagramFollowers"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, lang]);

  useEffect(() => {
    const fetchKey = `${startDate}|${endDate}|${lang}`;
    if (lastFetchKeyRef.current === fetchKey) {return;}
    lastFetchKeyRef.current = fetchKey;
    fetchReport();
  }, [fetchReport, startDate, endDate, lang]);

  const kpis = (data?.kpis ?? []).map(normalizeKpiCard);
  const rawGenderDemographics = data?.gender_demographics ?? [];
  const hasCountLikePercent = rawGenderDemographics.some(
    g => Number(g.percent ?? 0) > 100
  );
  const totalGenderValue = rawGenderDemographics.reduce(
    (sum, g) => sum + Number(g.percent ?? 0),
    0
  );
  const genderDemographics = rawGenderDemographics.map(g => {
    const rawPercent = Number(g.percent ?? 0);
    const normalizedPercent =
      hasCountLikePercent && totalGenderValue > 0
        ? (rawPercent / totalGenderValue) * 100
        : rawPercent;

    return {
      key: g.key,
      percent: Number(normalizedPercent.toFixed(2)),
      comparisonLabel: g.comparison_label ?? "vs last period",
      ages: g.ages ?? [],
    };
  });
  const countries = data?.countries ?? [];
  const cities = data?.cities ?? [];
  const followerTrend = getFollowersTrendSeries(data?.follower_trend, trendGranularity);

  return {
    kpis,
    genderDemographics,
    countries,
    cities,
    followerTrend,
    loading,
    error,
    refetch: fetchReport,
  };
}
